import { useEffect, useState, useRef, useMemo } from 'react';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import {
  MdCheckCircle,
  MdClose,
  MdRemoveRedEye,
  MdSearch,
  MdDelete,
  MdVideoLibrary,
  MdPictureAsPdf,
  MdSlideshow,
  MdArticle,
  MdLibraryBooks,
  MdOpenInNew,
  MdDownload,
  MdPlayCircle,
  MdSchedule,
  MdPerson,
  MdBook,
  MdLabel,
  MdSchool,
  MdUndo,
  MdAutoAwesome,
  MdUploadFile,
  MdEdit,
  MdSave,
  MdCloudUpload,
  MdImage,
  MdArrowBack,
  MdGridView,
  MdViewList,
  MdChevronRight,
  MdMenuBook,
  MdTableChart,
} from 'react-icons/md';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import {
  approveContent,
  calculateContentStats,
  rejectContent,
  revertToPending,
  subscribeToContentApprovals,
  deleteContent,
  deleteMultipleContent,
  updateContent,
} from '../../services/contentApprovalService';
import {
  extractDocumentText,
  extractTextFromPdfInBrowser,
  chunkLessonContent,
} from '../../services/aiService';
import { uploadLibraryFile } from '../../services/libraryService';

/* ─────────────────────────────────────────────
   Helper: detect & normalise video embed URLs
───────────────────────────────────────────── */
const getVideoEmbedUrl = (url) => {
  if (!url) return null;

  // YouTube
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0&modestbranding=1`;

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  // Google Drive
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;

  // Cloudinary / direct mp4
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) return url;

  return null;
};

/* ─────────────────────────────────────────────
   Helper: build PDF viewer URL
───────────────────────────────────────────── */
const getPdfViewerUrl = (url) => {
  if (!url) return null;
  // Google Drive PDF
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  // Default: Mozilla PDF.js
  return `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(url)}`;
};

/* ─────────────────────────────────────────────
   Helper: build PPT/DOC viewer URL
───────────────────────────────────────────── */
const getPptViewerUrl = (url) => {
  if (!url) return null;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
};

/* ─────────────────────────────────────────────
   Helper: detect image URLs (Cloudinary, direct extensions)
───────────────────────────────────────────── */
const isImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(url) || url.includes('cloudinary.com') || url.includes('/image/upload/');
};

/* ─────────────────────────────────────────────
   Lesson-type icon + colour
───────────────────────────────────────────── */
const getLessonTypeInfo = (lessonType, type) => {
  if (type === 'Topic' || lessonType === 'Topic') {
    return { icon: MdLibraryBooks, colour: '#4F46E5', bg: '#EEF2FF', label: 'Curriculum Topic' };
  }
  switch (lessonType) {
    case 'Video':
      return { icon: MdVideoLibrary, colour: '#3B82F6', bg: '#EFF6FF', label: 'Video' };
    case 'PDF':
      return { icon: MdPictureAsPdf, colour: '#EF4444', bg: '#FEF2F2', label: 'PDF Document' };
    case 'PPT':
      return { icon: MdSlideshow, colour: '#F59E0B', bg: '#FFFBEB', label: 'Presentation' };
    case 'Mixed':
      return { icon: MdLibraryBooks, colour: '#8B5CF6', bg: '#F5F3FF', label: 'Mixed Content' };
    default:
      return { icon: MdArticle, colour: '#10B981', bg: '#ECFDF5', label: 'Text Lesson' };
  }
};

/* ─────────────────────────────────────────────
   ContentPreview Component
───────────────────────────────────────────── */
const ContentPreview = ({ content, onContentUpdated }) => {
  const contentData = content?.contentData || {};
  const lessonType = contentData?.lessonType || 'Text';
  const mediaUrl = contentData?.mediaUrl || '';
  const externalLink = contentData?.externalLink || '';
  const initialText = contentData?.extractedText || contentData?.body || content?.extractedText || content?.body || '';

  const [liveExtractedText, setLiveExtractedText] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractStatus, setExtractStatus] = useState('');
  const [extractError, setExtractError] = useState('');
  const [isEditingText, setIsEditingText] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [editorTab, setEditorTab] = useState('write'); // 'write' | 'preview'
  const [isSavingText, setIsSavingText] = useState(false);
  const [editSuccessMsg, setEditSuccessMsg] = useState('');
  const localFileInputRef = useRef(null);

  const [isUploadingDiagram, setIsUploadingDiagram] = useState(false);
  const [diagramUploadError, setDiagramUploadError] = useState('');
  const diagramFileInputRef = useRef(null);

  const explicitDiagram = contentData?.diagramUrl || content?.diagramUrl || contentData?.imageUrl || content?.imageUrl || '';
  let activeDiagramUrl = explicitDiagram;
  if (!activeDiagramUrl && isImageUrl(mediaUrl)) {
    activeDiagramUrl = mediaUrl;
  }
  if (!activeDiagramUrl && isImageUrl(externalLink)) {
    activeDiagramUrl = externalLink;
  }
  if (!activeDiagramUrl && initialText) {
    const imgMatch = initialText.match(/https?:\/\/[^\s"<>)]+(?:\.png|\.jpe?g|\.webp|\.gif|cloudinary\.com[^\s"<>)]*)/i);
    if (imgMatch) {
      activeDiagramUrl = imgMatch[0];
    }
  }
  const activeDiagramCaption = contentData?.diagramCaption || content?.diagramCaption || '';
  const activeDiagramSpoken = contentData?.diagramSpoken || content?.diagramSpoken || '';

  const handleDiagramUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingDiagram(true);
    setDiagramUploadError('');
    try {
      const url = await uploadLibraryFile(file, 'smartbrain/diagrams');
      if (url && content?.id) {
        const newCap = activeDiagramCaption || `Figure: ${content.title || 'Lesson Diagram'}`;
        await updateContent(content.id, {
          'contentData.diagramUrl': url,
          'contentData.diagramCaption': newCap,
          diagramUrl: url,
          diagramCaption: newCap,
        });
        if (onContentUpdated) {
          onContentUpdated({
            ...content,
            diagramUrl: url,
            diagramCaption: newCap,
            contentData: {
              ...contentData,
              diagramUrl: url,
              diagramCaption: newCap,
            },
          });
        }
      }
    } catch (err) {
      console.error('Failed to upload diagram:', err);
      setDiagramUploadError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploadingDiagram(false);
      if (diagramFileInputRef.current) diagramFileInputRef.current.value = '';
    }
  };

  const handleRemoveDiagram = async () => {
    if (!content?.id) return;
    try {
      await updateContent(content.id, {
        'contentData.diagramUrl': '',
        'contentData.diagramCaption': '',
        'contentData.diagramSpoken': '',
        diagramUrl: '',
        diagramCaption: '',
        diagramSpoken: '',
      });
      if (onContentUpdated) {
        onContentUpdated({
          ...content,
          diagramUrl: '',
          diagramCaption: '',
          diagramSpoken: '',
          contentData: {
            ...contentData,
            diagramUrl: '',
            diagramCaption: '',
            diagramSpoken: '',
          },
        });
      }
    } catch (err) {
      console.error('Failed to remove diagram:', err);
    }
  };

  const activeTableTitle = contentData?.tableTitle || content?.tableTitle || '';
  const activeTableContent = contentData?.tableContent || content?.tableContent || '';
  const activeTableSpoken = contentData?.tableSpoken || content?.tableSpoken || '';

  const [isEditingTable, setIsEditingTable] = useState(false);
  const [editTableTitle, setEditTableTitle] = useState('');
  const [editTableContent, setEditTableContent] = useState('');
  const [editTableSpoken, setEditTableSpoken] = useState('');
  const [tableSaveLoading, setTableSaveLoading] = useState(false);
  const [tableSuccessMsg, setTableSuccessMsg] = useState('');

  const handleStartEditTable = () => {
    setEditTableTitle(activeTableTitle);
    setEditTableContent(activeTableContent);
    setEditTableSpoken(activeTableSpoken);
    setIsEditingTable(true);
    setTableSuccessMsg('');
  };

  const handleSaveTable = async () => {
    if (!content?.id) return;
    setTableSaveLoading(true);
    try {
      await updateContent(content.id, {
        'contentData.tableTitle': editTableTitle.trim(),
        'contentData.tableContent': editTableContent.trim(),
        'contentData.tableSpoken': editTableSpoken.trim(),
        tableTitle: editTableTitle.trim(),
        tableContent: editTableContent.trim(),
        tableSpoken: editTableSpoken.trim(),
      });
      if (onContentUpdated) {
        onContentUpdated({
          ...content,
          tableTitle: editTableTitle.trim(),
          tableContent: editTableContent.trim(),
          tableSpoken: editTableSpoken.trim(),
          contentData: {
            ...contentData,
            tableTitle: editTableTitle.trim(),
            tableContent: editTableContent.trim(),
            tableSpoken: editTableSpoken.trim(),
          },
        });
      }
      setIsEditingTable(false);
      setTableSuccessMsg('Lesson table updated successfully!');
      setTimeout(() => setTableSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Failed to save table:', err);
    } finally {
      setTableSaveLoading(false);
    }
  };

  const handleRemoveTable = async () => {
    if (!content?.id) return;
    try {
      await updateContent(content.id, {
        'contentData.tableTitle': '',
        'contentData.tableContent': '',
        'contentData.tableSpoken': '',
        tableTitle: '',
        tableContent: '',
        tableSpoken: '',
      });
      if (onContentUpdated) {
        onContentUpdated({
          ...content,
          tableTitle: '',
          tableContent: '',
          tableSpoken: '',
          contentData: {
            ...contentData,
            tableTitle: '',
            tableContent: '',
            tableSpoken: '',
          },
        });
      }
      setIsEditingTable(false);
    } catch (err) {
      console.error('Failed to remove table:', err);
    }
  };

  useEffect(() => {
    setLiveExtractedText('');
    setIsEditingText(false);
    setEditSuccessMsg('');
    setEditedText('');
    setDiagramUploadError('');
  }, [content?.id]);

  const effectiveBodyText = liveExtractedText || initialText;
  const questions = Array.isArray(contentData?.questions) ? contentData.questions : [];
  const rawType = (content?.type || content?.submissionType || '').toLowerCase().trim();
  const submissionType = content?.type || (rawType === 'quiz' ? 'Quiz' : rawType === 'topic' ? 'Topic' : 'Lesson');
  const isTopic = rawType === 'topic' || submissionType === 'Topic';
  const isQuiz = rawType === 'quiz' || submissionType === 'Quiz' || questions.length > 0;
  const isLesson = !isTopic && !isQuiz;
  const typeInfo = getLessonTypeInfo(lessonType, submissionType);
  const TypeIcon = typeInfo.icon;

  // Detect video from mediaUrl first, then from externalLink as fallback
  const mediaEmbedUrl = getVideoEmbedUrl(mediaUrl);
  const extEmbedUrl = getVideoEmbedUrl(externalLink);
  const activeVideoUrl = mediaEmbedUrl ? mediaUrl : (extEmbedUrl ? externalLink : '');
  const activeEmbedUrl = mediaEmbedUrl || extEmbedUrl;

  // externalLink shown as plain link only if it's not a video
  const showExternalLinkAsLink = externalLink && externalLink !== mediaUrl && !extEmbedUrl;

  const subtopics = Array.isArray(content?.subtopics) ? content.subtopics : [];
  const learningObjectives = Array.isArray(content?.learningObjectives) ? content.learningObjectives : [];
  const hasTopicDetails = isTopic && (subtopics.length > 0 || learningObjectives.length > 0 || content?.order);

  const subtopic = contentData?.subtopic || (Array.isArray(contentData?.subtopics) ? contentData.subtopics.join(', ') : '') || '';
  const isPdf = lessonType === 'PDF' || (contentData?.fileName && contentData.fileName.toLowerCase().endsWith('.pdf')) || (mediaUrl && mediaUrl.toLowerCase().includes('.pdf'));

  const hasBody = effectiveBodyText && effectiveBodyText.trim().length > 0;
  const hasQuestions = questions.length > 0;
  const hasAnything = hasBody || mediaUrl || externalLink || hasQuestions || hasTopicDetails;

  const handleExtractFromUrl = async () => {
    if (!mediaUrl) return;
    setIsExtracting(true);
    setExtractError('');
    setExtractStatus('Reading PDF pages and extracting text in browser...');
    try {
      let result = null;
      try {
        result = await extractTextFromPdfInBrowser(mediaUrl);
      } catch (browserErr) {
        console.warn('Direct in-browser fetch failed, trying backend extractor fallback:', browserErr);
        result = await extractDocumentText({ pdfUrl: mediaUrl });
      }

      const extracted = result?.text?.trim() || '';
      if (!extracted) {
        throw new Error('No readable text layer found in this PDF (it may be a scanned image).');
      }

      const chunks = chunkLessonContent(extracted);
      if (content?.id) {
        await updateContent(content.id, {
          'contentData.extractedText': extracted,
          'contentData.body': extracted,
          'contentData.textChunks': chunks,
          'contentData.isAIExtracted': true,
        });
      }

      setLiveExtractedText(extracted);
      const words = extracted.split(/\s+/).filter(Boolean).length;
      setExtractStatus(`Successfully extracted ${words.toLocaleString()} words across ${result.pageCount || 1} pages and saved to database!`);
    } catch (err) {
      console.error('Extraction error:', err);
      setExtractError(err.message || 'Failed to extract text. You can also pick the PDF from your device below.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleLocalFileExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsExtracting(true);
    setExtractError('');
    setExtractStatus('Extracting text from local PDF in browser...');
    try {
      const result = await extractTextFromPdfInBrowser(file);
      const extracted = result?.text?.trim() || '';
      if (!extracted) {
        throw new Error('No readable text layer found in this PDF.');
      }
      const chunks = chunkLessonContent(extracted);
      if (content?.id) {
        await updateContent(content.id, {
          'contentData.extractedText': extracted,
          'contentData.body': extracted,
          'contentData.textChunks': chunks,
          'contentData.isAIExtracted': true,
        });
      }
      setLiveExtractedText(extracted);
      const words = extracted.split(/\s+/).filter(Boolean).length;
      setExtractStatus(`Successfully extracted ${words.toLocaleString()} words across ${result.pageCount || 1} pages and saved to database!`);
    } catch (err) {
      console.error('Local file extraction error:', err);
      setExtractError(err.message || 'Failed to extract text from selected file.');
    } finally {
      setIsExtracting(false);
      if (localFileInputRef.current) localFileInputRef.current.value = '';
    }
  };

  const handleSaveEditedText = async () => {
    if (!content?.id) return;
    setIsSavingText(true);
    setEditSuccessMsg('');
    try {
      const trimmedText = (editedText || '').trim();
      const chunks = chunkLessonContent(trimmedText);

      await updateContent(content.id, {
        'contentData.extractedText': trimmedText,
        'contentData.body': trimmedText,
        'contentData.textChunks': chunks,
        extractedText: trimmedText,
        body: trimmedText,
      });

      setLiveExtractedText(trimmedText);
      setIsEditingText(false);
      setEditSuccessMsg('✅ Lesson notes saved and re-indexed for AI Teacher successfully!');

      if (onContentUpdated) {
        onContentUpdated({
          extractedText: trimmedText,
          body: trimmedText,
          contentData: {
            ...contentData,
            extractedText: trimmedText,
            body: trimmedText,
            textChunks: chunks,
          },
        });
      }
    } catch (err) {
      console.error('Error saving edited lesson note:', err);
      alert('Failed to save lesson note: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSavingText(false);
    }
  };

  return (
    <div className="content-preview-block">
      <div className="preview-type-badge" style={{ background: typeInfo.bg, color: typeInfo.colour }}>
        <TypeIcon size={16} />
        <span>{submissionType === 'Quiz' ? 'Quiz' : typeInfo.label}</span>
      </div>

      {/* ── Subtopic Focus Badge ── */}
      {subtopic && (
        <div className="mb-4 p-3 bg-blue-50/80 border border-blue-200 rounded-xl flex items-center gap-2">
          <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">📌 Focus Subtopic:</span>
          <span className="text-xs font-bold text-darkNavy">{subtopic}</span>
        </div>
      )}

      {/* ── Topic / Curriculum Details ── */}
      {isTopic && (
        <div className="mb-6 space-y-4">
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
            <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2">
              📚 Curriculum Topic Position
            </p>
            <p className="text-sm text-slate-700 font-medium">
              Order: <span className="font-bold text-slate-900">Topic {content.order || 1}</span> · Term: <span className="font-bold text-slate-900">{content.term || 'Term 1'}</span>
            </p>
          </div>

          {subtopics.length > 0 && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
              <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-3">
                🔖 Subtopics Covered ({subtopics.length})
              </p>
              <ul className="space-y-1.5 pl-4 list-disc text-sm text-slate-700 font-medium">
                {subtopics.map((st, i) => (
                  <li key={i}>{st}</li>
                ))}
              </ul>
            </div>
          )}

          {learningObjectives.length > 0 && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-3">
                🎯 Learning Objectives
              </p>
              <ul className="space-y-1.5 pl-4 list-disc text-sm text-slate-700 font-medium">
                {learningObjectives.map((obj, i) => (
                  <li key={i}>{obj}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Quiz Questions ── */}
      {hasQuestions && (
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
            📝 Quiz Questions ({questions.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '420px', overflowY: 'auto' }}>
            {questions.map((q, qi) => (
              <div
                key={qi}
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '14px 16px',
                }}
              >
                <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: '10px', fontSize: '0.9rem', display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ color: '#6366f1', flexShrink: 0 }}>Q{qi + 1}.</span>
                  <Latex>{q.questionText || q.question || '(No question text)'}</Latex>
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {(q.options || []).map((opt, oi) => {
                    const isCorrect = oi === (q.correctAnswerIndex ?? q.correctOptionIndex);
                    return (
                      <div
                        key={oi}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '7px 12px',
                          borderRadius: '8px',
                          background: isCorrect ? '#d1fae5' : '#fff',
                          border: isCorrect ? '1px solid #6ee7b7' : '1px solid #e2e8f0',
                          fontSize: '0.85rem',
                          fontWeight: isCorrect ? 700 : 400,
                          color: isCorrect ? '#065f46' : '#374151',
                        }}
                      >
                        <span style={{
                          width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                          background: isCorrect ? '#10b981' : '#e5e7eb',
                          color: isCorrect ? '#fff' : '#6b7280',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.75rem', fontWeight: 700,
                        }}>
                          {String.fromCharCode(65 + oi)}
                        </span>
                        <Latex>{opt}</Latex>
                        {isCorrect && (
                          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#059669', fontWeight: 700 }}>
                            ✓ Correct
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Non-video mediaUrl (PDF / file upload) ── */}
      {mediaUrl && !mediaEmbedUrl && (
        <div className="media-url-row mb-5 p-4 border rounded-xl bg-gray-50">
          <p className="text-sm font-bold text-gray-700 mb-1">
            {contentData?.fileName ? '📎 Attached File:' : '🔗 Attached Link:'}
          </p>
          {contentData?.fileName && (
            <p className="text-xs text-gray-500 mb-3 font-mono">{contentData.fileName}</p>
          )}
          <div className="flex gap-3 flex-wrap items-center">
            <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="open-link-btn outline">
              <MdOpenInNew size={16} /> Open File
            </a>
            <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="open-link-btn">
              <MdDownload size={16} /> Download
            </a>

            {/* Instant Text Extraction button if PDF has no text extracted yet */}
            {isPdf && !hasBody && (
              <button
                type="button"
                onClick={handleExtractFromUrl}
                disabled={isExtracting}
                className="px-4 py-2 bg-primary hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                {isExtracting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Extracting Text...
                  </>
                ) : (
                  <>
                    <MdAutoAwesome size={15} /> Extract Text from PDF
                  </>
                )}
              </button>
            )}

            {isPdf && !hasBody && (
              <>
                <input
                  type="file"
                  accept=".pdf"
                  ref={localFileInputRef}
                  onChange={handleLocalFileExtract}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => localFileInputRef.current?.click()}
                  disabled={isExtracting}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-semibold transition-all flex items-center gap-1"
                  title="If the storage URL has CORS restrictions, extract from your local file directly"
                >
                  <MdUploadFile size={15} /> From Device
                </button>
              </>
            )}
          </div>

          {extractStatus && (
            <div className="mt-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 font-semibold flex items-center gap-1.5">
              <MdCheckCircle className="text-emerald-600 flex-shrink-0" size={16} />
              <span>{extractStatus}</span>
            </div>
          )}

          {extractError && (
            <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-semibold">
              ⚠️ {extractError}
            </div>
          )}
        </div>
      )}

      {/* ── Lesson Note Content & Admin In-Place Editor ── */}
      {(hasBody || submissionType === 'Lesson') && (
        <div className="text-body-preview mb-6">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                📄 Lesson Note Content (AI Knowledge Base)
              </p>
              {hasBody && (
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  {effectiveBodyText.split(/\s+/).filter(Boolean).length.toLocaleString()} words · Ready for AI Teacher
                </span>
              )}
            </div>

            {!isEditingText && (
              <button
                type="button"
                onClick={() => {
                  setEditedText(effectiveBodyText);
                  setIsEditingText(true);
                  setEditorTab('write');
                  setEditSuccessMsg('');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-bold transition-all shadow-sm cursor-pointer"
              >
                <MdEdit size={14} />
                {hasBody ? 'Edit Lesson Notes' : '+ Add Lesson Notes'}
              </button>
            )}
          </div>

          {editSuccessMsg && (
            <div className="mb-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-800 flex items-center justify-between">
              <span>{editSuccessMsg}</span>
              <button
                type="button"
                onClick={() => setEditSuccessMsg('')}
                className="text-emerald-600 hover:text-emerald-900 ml-2 font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}

          {isEditingText ? (
            <div className="bg-white border-2 border-indigo-200 rounded-xl overflow-hidden shadow-sm transition-all">
              {/* Tab Bar */}
              <div className="flex items-center justify-between px-3 py-2 bg-indigo-50/70 border-b border-indigo-100">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditorTab('write')}
                    style={{
                      padding: '5px 14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      borderRadius: '6px',
                      border: editorTab === 'write' ? '1px solid #C7D2FE' : '1px solid transparent',
                      backgroundColor: editorTab === 'write' ? '#FFFFFF' : 'transparent',
                      color: editorTab === 'write' ? '#4338CA' : '#64748B',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    ✏️ Edit Text
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorTab('preview')}
                    style={{
                      padding: '5px 14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      borderRadius: '6px',
                      border: editorTab === 'preview' ? '1px solid #C7D2FE' : '1px solid transparent',
                      backgroundColor: editorTab === 'preview' ? '#FFFFFF' : 'transparent',
                      color: editorTab === 'preview' ? '#4338CA' : '#64748B',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    👁️ Preview (with LaTeX)
                  </button>
                </div>
                <span className="text-[11px] font-semibold text-slate-500">
                  {editedText.split(/\s+/).filter(Boolean).length} words · {editedText.length} chars
                </span>
              </div>

              {/* Editor / Preview Body */}
              {editorTab === 'write' ? (
                <textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  placeholder="Paste or write lesson notes here... supports plain text, formulas, and LaTeX ($...$)"
                  rows={12}
                  className="w-full p-3.5 text-slate-800 text-sm font-sans outline-none resize-y border-none focus:ring-0 leading-relaxed bg-slate-50/50"
                  style={{ minHeight: '220px', maxHeight: '480px' }}
                />
              ) : (
                <div
                  className="p-4 bg-slate-50/50 text-slate-800 text-sm leading-relaxed overflow-y-auto"
                  style={{ minHeight: '220px', maxHeight: '480px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {editedText ? (
                    <Latex>{editedText}</Latex>
                  ) : (
                    <span className="text-gray-400 italic">No content to preview. Type something in the Edit Text tab.</span>
                  )}
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-100/80 border-t border-slate-200">
                <span className="text-xs text-slate-500">
                  Updates database & re-chunks immediately for AI Teacher.
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingText(false);
                      setEditedText(effectiveBodyText);
                    }}
                    disabled={isSavingText}
                    style={{
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#475569',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #CBD5E1',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F1F5F9')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#FFFFFF')}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEditedText}
                    disabled={isSavingText}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 20px',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      backgroundColor: isSavingText ? '#94A3B8' : '#059669',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isSavingText ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 8px rgba(5, 150, 105, 0.4)',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSavingText) e.currentTarget.style.backgroundColor = '#047857';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSavingText) e.currentTarget.style.backgroundColor = '#059669';
                    }}
                  >
                    <MdSave size={16} />
                    {isSavingText ? 'Saving & Re-indexing...' : 'Save Lesson Notes'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            hasBody ? (
              <div
                style={{
                  lineHeight: '1.8',
                  color: '#1e293b',
                  fontSize: '0.92rem',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px',
                  maxHeight: '340px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                <Latex>{effectiveBodyText}</Latex>
              </div>
            ) : (
              <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-xl text-center text-xs text-amber-800">
                No lesson notes currently attached to this submission. Click <strong>+ Add Lesson Notes</strong> above to add text directly for the AI Teacher.
              </div>
            )
          )}
        </div>
      )}

      {/* ── Lesson Diagram & Visual Aid (Cloudinary) ── */}
      {(isLesson || Boolean(activeDiagramUrl)) && (
        <div className="mb-6 p-4 border-2 border-teal-300 bg-teal-50/50 rounded-xl space-y-3 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-teal-950 uppercase tracking-wider flex items-center gap-1.5">
              🖼️ Lesson Diagram & Visual Aid
            </span>
            {activeDiagramUrl ? (
              <span className="text-[11px] font-bold text-teal-700 bg-teal-100 border border-teal-300 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-teal-600 animate-pulse"></span>
                Live on Mobile App
              </span>
            ) : (
              <span className="text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-2.5 py-0.5 rounded-full">
                ⚠️ No Image Attached in Database
              </span>
            )}
          </div>

          <input
            type="file"
            ref={diagramFileInputRef}
            onChange={handleDiagramUpload}
            accept="image/*"
            className="hidden"
          />

          {activeDiagramUrl ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white p-4 rounded-xl border border-teal-200 shadow-sm">
              <a
                href={activeDiagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Click to view full image in new tab"
                className="group relative block flex-shrink-0"
              >
                <img
                  src={activeDiagramUrl}
                  alt="Diagram Preview"
                  className="max-h-56 max-w-full sm:max-w-xs object-contain rounded-lg border border-teal-100 bg-gray-50 group-hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/400x250/e2e8f0/475569?text=Image+Load+Error';
                  }}
                />
                <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                  🔍 Zoom
                </span>
              </a>
              <div className="space-y-2 min-w-0 flex-1">
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {activeDiagramCaption || `Figure: ${content.title || 'Lesson Diagram'}`}
                  </p>
                </div>
                {activeDiagramSpoken && (
                  <p className="text-xs text-teal-900 bg-teal-50/80 p-2.5 rounded-lg border border-teal-200 italic">
                    Teacher Narration: "{activeDiagramSpoken}"
                  </p>
                )}
                <div className="flex items-center gap-3 pt-1 flex-wrap">
                  <a
                    href={activeDiagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 hover:text-teal-900 hover:underline"
                  >
                    <MdOpenInNew size={14} /> Open Full Image
                  </a>
                  <button
                    type="button"
                    onClick={() => diagramFileInputRef.current?.click()}
                    disabled={isUploadingDiagram}
                    className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer"
                  >
                    <MdCloudUpload size={14} />
                    {isUploadingDiagram ? 'Uploading...' : 'Replace Image'}
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveDiagram}
                    className="text-xs font-bold text-red-600 hover:text-red-800 hover:underline cursor-pointer"
                  >
                    Remove Image
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-white border-2 border-dashed border-teal-300 rounded-xl">
              <div>
                <p className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                  <MdImage className="text-teal-600" size={16} />
                  No diagram or image is currently stored for this lesson
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Click below to upload an image — it will instantly link to this lesson and display on the student's mobile app.
                </p>
              </div>
              <button
                type="button"
                onClick={() => diagramFileInputRef.current?.click()}
                disabled={isUploadingDiagram}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <MdCloudUpload size={16} />
                {isUploadingDiagram ? 'Uploading...' : '+ Attach Diagram Image'}
              </button>
            </div>
          )}

          {diagramUploadError && (
            <p className="text-xs font-bold text-red-600 bg-red-50 p-2 rounded-lg border border-red-200">
              ⚠️ {diagramUploadError}
            </p>
          )}
        </div>
      )}

      {/* ── Lesson Table & Comparison (AI Tutor) ── */}
      <div className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-xl space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <MdTableChart className="text-emerald-700" size={18} />
            <span className="text-xs font-bold text-emerald-900 uppercase tracking-wider">
              Lesson Table / Comparison (AI Tutor)
            </span>
          </div>
          {activeTableContent ? (
            <span className="text-[11px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 px-2.5 py-0.5 rounded-full">
              ✓ Attached in Database
            </span>
          ) : (
            <span className="text-[11px] font-semibold text-gray-600 bg-gray-100 border border-gray-300 px-2.5 py-0.5 rounded-full">
              AI Auto-generates when comparative
            </span>
          )}
        </div>

        {tableSuccessMsg && (
          <p className="text-xs font-bold text-emerald-700 bg-emerald-100 p-2 rounded-lg border border-emerald-300">
            ✓ {tableSuccessMsg}
          </p>
        )}

        {isEditingTable ? (
          <div className="bg-white p-4 rounded-xl border border-emerald-300 space-y-3 shadow-sm">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Table Title
              </label>
              <input
                type="text"
                placeholder="e.g. Differences Between Plant and Animal Cells"
                value={editTableTitle}
                onChange={(e) => setEditTableTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-600 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Table Content or Hints (Markdown or Pipe format)
              </label>
              <textarea
                rows={4}
                placeholder={`| Feature | Plant Cell | Animal Cell |
| Cell Wall | Present | Absent |
| Chloroplast | Present | Absent |`}
                value={editTableContent}
                onChange={(e) => setEditTableContent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-600 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Spoken Guidance for AI Voice (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Notice how plant cells have cell walls and chloroplasts while animal cells do not..."
                value={editTableSpoken}
                onChange={(e) => setEditTableSpoken(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-600 outline-none transition-all"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleSaveTable}
                disabled={tableSaveLoading}
                className="px-4 py-2 bg-primary hover:bg-blue-700 active:scale-95 text-white rounded-lg text-xs font-bold transition-all shadow flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
              >
                <MdSave size={14} /> {tableSaveLoading ? 'Saving...' : 'Save Table'}
              </button>
              <button
                type="button"
                onClick={() => setIsEditingTable(false)}
                disabled={tableSaveLoading}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition-all cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : activeTableContent ? (
          <div className="bg-white p-4 rounded-xl border border-emerald-200 shadow-sm space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-xs font-bold text-gray-900">
                {activeTableTitle || 'Lesson Comparison Table'}
              </h4>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleStartEditTable}
                  className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900 hover:underline cursor-pointer"
                >
                  <MdEdit size={13} /> Edit
                </button>
                <button
                  type="button"
                  onClick={handleRemoveTable}
                  className="text-xs font-bold text-red-600 hover:text-red-800 hover:underline cursor-pointer"
                >
                  Remove
                </button>
              </div>
            </div>
            <pre className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-x-auto max-h-48">
              {activeTableContent}
            </pre>
            {activeTableSpoken && (
              <p className="text-xs text-emerald-900 bg-emerald-50/80 p-2.5 rounded-lg border border-emerald-200 italic">
                Teacher Narration: "{activeTableSpoken}"
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-white border-2 border-dashed border-emerald-300 rounded-xl">
            <div>
              <p className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                <MdTableChart className="text-emerald-600" size={16} />
                No custom table is stored for this lesson
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                The AI Tutor will automatically construct a comparison table if the concept requires it. Or click below to specify one.
              </p>
            </div>
            <button
              type="button"
              onClick={handleStartEditTable}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm cursor-pointer whitespace-nowrap flex-shrink-0"
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              <MdEdit size={14} /> + Attach / Edit Table
            </button>
          </div>
        )}
      </div>

      {/* ── Video (from mediaUrl or externalLink) ── */}
      {activeEmbedUrl && (
        <div className="mt-2">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">🎬 Video</p>
          <div className="video-wrapper">
            {/\.(mp4|webm|ogg)(\?.*)?$/i.test(activeVideoUrl) ? (
              <video controls className="video-player" src={activeVideoUrl}>
                Your browser does not support the video tag.
              </video>
            ) : (
              <iframe
                src={activeEmbedUrl}
                title="Video Preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-iframe"
              />
            )}
          </div>
        </div>
      )}

      {/* ── Plain externalLink (not a video) ── */}
      {showExternalLinkAsLink && (
        <div className="media-url-row mt-4 p-4 border rounded-xl bg-gray-50">
          <p className="text-sm font-bold text-gray-700 mb-2">🔗 External Link:</p>
          <div className="flex gap-3">
            <a href={externalLink} target="_blank" rel="noopener noreferrer" className="open-link-btn outline">
              <MdOpenInNew size={16} /> Open External Link
            </a>
          </div>
        </div>
      )}

      {/* ── Nothing at all ── */}
      {!hasAnything && (
        <div className="text-body-preview">
          <p className="no-text-content">No content provided for this lesson.</p>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Helpers: Subject Name Resolution & Theming
───────────────────────────────────────────── */
const getContentSubject = (item) => {
  return (
    item?.subjectName ||
    item?.subject ||
    (item?.subjectId ? (item.subjectId.charAt(0).toUpperCase() + item.subjectId.slice(1)) : '') ||
    'General'
  ).trim();
};

/* ─────────────────────────────────────────────
   5 Curated Rich Gradient Palettes (Strictly 5 Colors, Never White)
───────────────────────────────────────────── */
const FIVE_CARD_THEMES = {
  // 1. Purple Gradient (Chemistry, etc.)
  purple: {
    gradientStyle: 'linear-gradient(135deg, #7e22ce 0%, #4338ca 50%, #701a75 100%)',
    border: 'border-purple-400/40 hover:border-purple-200',
    shadow: 'shadow-lg shadow-purple-950/20 hover:shadow-xl hover:shadow-purple-700/30',
    iconBg: 'bg-white/20 text-white border border-white/20 backdrop-blur-md',
    subtitleColor: 'text-purple-200',
    arrowBg: 'bg-white/20 text-white group-hover:bg-white group-hover:text-purple-900',
    actionText: 'text-purple-100 group-hover:text-white',
    borderColor: 'border-white/20',
  },
  // 2. Green Gradient (English Language, Biology, Agriculture, etc.)
  green: {
    gradientStyle: 'linear-gradient(135deg, #059669 0%, #047857 50%, #064e3b 100%)',
    border: 'border-emerald-400/40 hover:border-emerald-200',
    shadow: 'shadow-lg shadow-emerald-950/20 hover:shadow-xl hover:shadow-emerald-700/30',
    iconBg: 'bg-white/20 text-white border border-white/20 backdrop-blur-md',
    subtitleColor: 'text-emerald-100',
    arrowBg: 'bg-white/20 text-white group-hover:bg-white group-hover:text-emerald-900',
    actionText: 'text-emerald-100 group-hover:text-white',
    borderColor: 'border-white/20',
  },
  // 3. Orange / Amber Gradient (Financial Accounting, Commerce, Economics)
  orange: {
    gradientStyle: 'linear-gradient(135deg, #d97706 0%, #ea580c 50%, #9a3412 100%)',
    border: 'border-amber-400/40 hover:border-amber-200',
    shadow: 'shadow-lg shadow-amber-950/20 hover:shadow-xl hover:shadow-orange-700/30',
    iconBg: 'bg-white/20 text-white border border-white/20 backdrop-blur-md',
    subtitleColor: 'text-amber-100',
    arrowBg: 'bg-white/20 text-white group-hover:bg-white group-hover:text-amber-900',
    actionText: 'text-amber-100 group-hover:text-white',
    borderColor: 'border-white/20',
  },
  // 4. Blue Gradient (Mathematics, Computer Studies, Further Maths)
  blue: {
    gradientStyle: 'linear-gradient(135deg, #1d4ed8 0%, #3730a3 50%, #0369a1 100%)',
    border: 'border-blue-400/40 hover:border-blue-200',
    shadow: 'shadow-lg shadow-blue-950/20 hover:shadow-xl hover:shadow-blue-700/30',
    iconBg: 'bg-white/20 text-white border border-white/20 backdrop-blur-md',
    subtitleColor: 'text-blue-200',
    arrowBg: 'bg-white/20 text-white group-hover:bg-white group-hover:text-blue-900',
    actionText: 'text-blue-100 group-hover:text-white',
    borderColor: 'border-white/20',
  },
  // 5. Rose / Crimson Gradient (Physics, Government, Civic, etc.)
  rose: {
    gradientStyle: 'linear-gradient(135deg, #be123c 0%, #b91c1c 50%, #881337 100%)',
    border: 'border-rose-400/40 hover:border-rose-200',
    shadow: 'shadow-lg shadow-rose-950/20 hover:shadow-xl hover:shadow-rose-700/30',
    iconBg: 'bg-white/20 text-white border border-white/20 backdrop-blur-md',
    subtitleColor: 'text-rose-200',
    arrowBg: 'bg-white/20 text-white group-hover:bg-white group-hover:text-rose-900',
    actionText: 'text-rose-100 group-hover:text-white',
    borderColor: 'border-white/20',
  },
};

const getSubjectTheme = (name = '') => {
  const lower = (name || '').toLowerCase().trim();

  // Explicit mapping for standard subjects
  if (lower.includes('chem')) return FIVE_CARD_THEMES.purple;
  if (lower.includes('eng') || lower.includes('lit')) return FIVE_CARD_THEMES.green;
  if (lower.includes('account') || lower.includes('comm') || lower.includes('econ') || lower.includes('finan') || lower.includes('bus')) return FIVE_CARD_THEMES.orange;
  if (lower.includes('math')) return FIVE_CARD_THEMES.blue;
  if (lower.includes('phys') || lower.includes('gov') || lower.includes('civic') || lower.includes('hist')) return FIVE_CARD_THEMES.rose;

  // Fallback to one of the 5 colors using character hash
  const paletteKeys = ['purple', 'green', 'orange', 'blue', 'rose'];
  const charSum = lower.split('').reduce((sum, c) => sum + c.charCodeAt(0), 0);
  return FIVE_CARD_THEMES[paletteKeys[charSum % paletteKeys.length]];
};

/* ─────────────────────────────────────────────
   Main ContentApproval Page
───────────────────────────────────────────── */
const ContentApproval = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [editTitle, setEditTitle] = useState('');
  const [editTerm, setEditTerm] = useState('');
  const [editTimeLimit, setEditTimeLimit] = useState('');
  const [editStatus, setEditStatus] = useState('pending');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [viewMode, setViewMode] = useState('subjects'); // 'subjects' | 'all-table'

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToContentApprovals((data) => {
      // Sort: First input (Lesson 1 / oldest) on top, recent input at bottom
      const sorted = [...data].sort((a, b) => {
        const orderA = a.order ?? a.contentData?.order;
        const orderB = b.order ?? b.contentData?.order;
        if (orderA != null && orderB != null && orderA !== orderB) {
          return orderA - orderB;
        }
        const getMs = (item) => {
          if (!item) return 0;
          const d = item.createdAt;
          if (d?.toDate) return d.toDate().getTime();
          if (d?.seconds) return d.seconds * 1000;
          if (typeof d === 'string') return new Date(d).getTime() || 0;
          return 0;
        };
        const timeA = getMs(a);
        const timeB = getMs(b);
        if (timeA && timeB && timeA !== timeB) {
          return timeA - timeB;
        }
        return 0;
      });

      setContents(sorted);
      setStats(calculateContentStats(data));
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleViewContent = (content) => {
    setSelectedContent(content);
    setEditTitle(content.title || '');
    setEditStatus(content.status || 'pending');
    setEditTerm(content.contentData?.term?.toString() || '1');
    setEditTimeLimit(content.contentData?.timeLimit?.toString() || '');
    setShowViewModal(true);
  };

  const handleApprove = async (content) => {
    setActionLoading(true);
    try {
      let updatedData = { ...content.contentData };
      let topLevelUpdates = {};
      let changed = false;

      if (editTitle && editTitle.trim() && editTitle.trim() !== content.title) {
        topLevelUpdates.title = editTitle.trim();
        updatedData.topicName = editTitle.trim();
        changed = true;
      }
      if (content.type === 'Lesson' && editTerm) {
         updatedData.term = parseInt(editTerm);
         changed = true;
      }
      if (content.type === 'Quiz' && editTimeLimit) {
         updatedData.timeLimit = parseInt(editTimeLimit);
         changed = true;
      }

      if (changed) {
         await updateContent(content.id, {
           ...topLevelUpdates,
           contentData: updatedData,
         });
      }

      await approveContent(content.id, 'Admin');
      setShowViewModal(false);
      setSelectedContent(null);
    } catch (error) {
      console.error('Error approving content:', error);
      alert('Failed to approve content');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await rejectContent(selectedContent.id, 'Admin', rejectReason);
      setShowRejectModal(false);
      setShowViewModal(false);
      setRejectReason('');
      setSelectedContent(null);
    } catch (error) {
      console.error('Error rejecting content:', error);
      alert('Failed to reject content');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevert = async (content) => {
    if (!window.confirm(`Revert "${content.title || 'this content'}" back to Pending Review?`)) return;
    setActionLoading(true);
    try {
      await revertToPending(content.id);
      alert('✅ Content reverted to Pending Review!');
      setShowViewModal(false);
      setSelectedContent(null);
    } catch (error) {
      console.error('Error reverting content:', error);
      alert('Failed to revert content status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content submission? This action cannot be undone.')) return;
    try {
      await deleteContent(contentId);
      setSelectedIds((prev) => prev.filter((id) => id !== contentId));
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Failed to delete content');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    if (!window.confirm(`⚠️ Are you sure you want to permanently delete ${count} selected ${count === 1 ? 'item' : 'items'} from the database? This action cannot be undone.`)) return;

    setActionLoading(true);
    try {
      await deleteMultipleContent(selectedIds);
      setSelectedIds([]);
      alert(`✅ Successfully deleted ${count} ${count === 1 ? 'item' : 'items'} from the database!`);
    } catch (error) {
      console.error('Error deleting multiple items:', error);
      alert('Failed to delete selected items from the database. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Admin override: save term/timeLimit/status/title on content
  const handleSaveOverride = async () => {
    if (!selectedContent) return;
    setActionLoading(true);
    try {
      const updatedData = { ...selectedContent.contentData };
      const topLevelUpdates = {};

      if (editTitle && editTitle.trim() && editTitle.trim() !== selectedContent.title) {
        topLevelUpdates.title = editTitle.trim();
        updatedData.topicName = editTitle.trim();
      }
      if (selectedContent.type === 'Lesson' && editTerm) {
        updatedData.term = parseInt(editTerm);
      }
      if (selectedContent.type === 'Quiz' && editTimeLimit) {
        updatedData.durationMinutes = parseInt(editTimeLimit);
        updatedData.timeLimit = parseInt(editTimeLimit);
      }
      await updateContent(selectedContent.id, {
        ...topLevelUpdates,
        contentData: updatedData,
      });

      if (editStatus && editStatus !== selectedContent.status) {
        if (editStatus === 'approved') {
          await approveContent(selectedContent.id, 'Admin');
        } else if (editStatus === 'rejected') {
          await rejectContent(selectedContent.id, 'Admin', 'Updated by Admin');
        } else if (editStatus === 'pending') {
          await revertToPending(selectedContent.id);
        }
      }

      alert('✅ Changes saved successfully!');
      setShowViewModal(false);
      setSelectedContent(null);
    } catch (error) {
      console.error('Error saving override:', error);
      alert('Failed to save changes');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredContents = contents.filter((content) => {
    const matchTab = activeTab === 'all' || content.status === activeTab;
    if (!matchTab) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const subName = (content.subjectName || content.subject || content.subjectId || '').toLowerCase();
    const topicName = (content.contentData?.topicName || content.topicName || '').toLowerCase();
    const subtopic = (content.contentData?.subtopic || '').toLowerCase();
    return (
      (content.title || '').toLowerCase().includes(term) ||
      subName.includes(term) ||
      topicName.includes(term) ||
      subtopic.includes(term) ||
      (content.teacherName || content.teacher || '').toLowerCase().includes(term)
    );
  });

  const subjectGroups = useMemo(() => {
    const groups = {};
    filteredContents.forEach((item) => {
      const sub = getContentSubject(item);
      if (!groups[sub]) {
        groups[sub] = {
          name: sub,
          items: [],
          topics: new Set(),
          pendingCount: 0,
          approvedCount: 0,
          rejectedCount: 0,
          teachers: new Set(),
        };
      }
      groups[sub].items.push(item);
      const topName = (item.contentData?.topicName || item.topicName || item.contentData?.topicTitle || item.title || '').trim();
      if (topName) groups[sub].topics.add(topName);
      if (item.status === 'pending') groups[sub].pendingCount++;
      if (item.status === 'approved') groups[sub].approvedCount++;
      if (item.status === 'rejected') groups[sub].rejectedCount++;
      const tName = item.teacherName || item.teacher;
      if (tName) groups[sub].teachers.add(tName);
    });
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredContents]);

  const displayedContents = useMemo(() => {
    if (viewMode === 'all-table' || !selectedSubject) {
      return filteredContents;
    }
    return filteredContents.filter((c) => getContentSubject(c) === selectedSubject);
  }, [viewMode, selectedSubject, filteredContents]);

  const lessonType = selectedContent?.contentData?.lessonType || 'Text';
  const typeInfo = getLessonTypeInfo(lessonType, selectedContent?.type);

  /* ── Table columns ── */
  const columns = [
    {
      header: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <input
            type="checkbox"
            checked={displayedContents.length > 0 && displayedContents.every((c) => selectedIds.includes(c.id))}
            ref={(el) => {
              if (el) {
                const allSelected = displayedContents.length > 0 && displayedContents.every((c) => selectedIds.includes(c.id));
                const someSelected = selectedIds.length > 0 && !allSelected;
                el.indeterminate = someSelected;
              }
            }}
            onChange={(e) => {
              if (e.target.checked) {
                const allVisibleIds = displayedContents.map((c) => c.id);
                setSelectedIds((prev) => Array.from(new Set([...prev, ...allVisibleIds])));
              } else {
                const visibleIdSet = new Set(displayedContents.map((c) => c.id));
                setSelectedIds((prev) => prev.filter((id) => !visibleIdSet.has(id)));
              }
            }}
            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#2563EB' }}
            title="Select all"
          />
        </div>
      ),
      accessor: 'select',
      width: '46px',
      render: (_, row) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id)}
            onChange={(e) => {
              e.stopPropagation();
              setSelectedIds((prev) =>
                prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]
              );
            }}
            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#2563EB' }}
          />
        </div>
      ),
    },
    {
      header: 'Topic',
      accessor: 'topicName',
      render: (value, row) => {
        const lt = row?.contentData?.lessonType || 'Text';
        const ti = getLessonTypeInfo(lt, row?.type);
        const TI = ti.icon;

        let topic = (
          value ||
          row?.contentData?.topicName ||
          row?.topicName ||
          row?.contentData?.topicTitle ||
          row?.topicTitle ||
          (row?.type === 'Topic' ? (row?.title || row?.contentData?.title) : '') ||
          ''
        ).trim();

        if (!topic) {
          const raw = (row?.title || row?.contentData?.title || 'General Curriculum').trim();
          topic = raw;
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8,
                background: ti.bg, color: ti.colour, flexShrink: 0,
              }}
            >
              <TI size={17} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontWeight: 700, color: '#0F172A', fontSize: 13.5 }}>
                {topic}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Lesson',
      accessor: 'title',
      render: (value, row) => {
        if (row?.type === 'Topic') {
          return (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700,
              background: '#EEF2FF', color: '#4F46E5',
            }}>
              Curriculum Topic
            </span>
          );
        }
        if (row?.type === 'Quiz') {
          return (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 700,
              background: '#FEF3C7', color: '#D97706',
            }}>
              CBT Quiz
            </span>
          );
        }

        const lessonTitle = (value || row?.contentData?.title || 'Untitled Lesson').trim();
        const lt = row?.contentData?.lessonType || 'Text';
        const ti = getLessonTypeInfo(lt);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontWeight: 600, color: '#0F172A', fontSize: 13.5 }}>
              {lessonTitle}
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', width: 'fit-content',
              padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
              background: ti.bg, color: ti.colour, border: `1px solid ${ti.colour}22`,
            }}>
              {ti.label}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Subject',
      accessor: 'subjectName',
      render: (value, row) => {
        const sub = value || row?.subject || (row?.subjectId ? (row.subjectId.charAt(0).toUpperCase() + row.subjectId.slice(1)) : 'N/A');
        return <span style={{ color: '#374151', fontWeight: 500 }}>{sub}</span>;
      },
    },
    {
      header: 'Teacher',
      accessor: 'teacherName',
      render: (value) => <span style={{ color: '#374151' }}>{value || 'N/A'}</span>,
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => {
        const map = {
          pending: { bg: '#FEF3C7', colour: '#D97706', label: 'Pending' },
          approved: { bg: '#D1FAE5', colour: '#059669', label: 'Approved' },
          rejected: { bg: '#FEE2E2', colour: '#DC2626', label: 'Rejected' },
        };
        const s = map[value] || map.pending;
        return (
          <span style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: s.bg, color: s.colour,
          }}>
            {s.label}
          </span>
        );
      },
    },
    {
      header: 'Submitted',
      accessor: 'createdAt',
      render: (value) => {
        const date = value?.toDate?.() || new Date(value);
        return (
          <span style={{ color: '#6B7280', fontSize: 13 }}>
            {date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        );
      },
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => handleViewContent(row)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: '#EFF6FF', color: '#2563EB', fontSize: 12, fontWeight: 600,
            }}
            title="Preview & Review"
          >
            <MdRemoveRedEye size={15} /> Preview
          </button>
          {row.status === 'pending' ? (
            <>
              <button
                onClick={() => {
                  if (window.confirm(`Approve and publish "${row.title || 'this content'}"?`)) {
                    handleApprove(row);
                  }
                }}
                style={{
                  padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: '#D1FAE5', color: '#059669', fontSize: 12, fontWeight: 600,
                }}
                title="Approve"
              >
                ✓ Approve
              </button>
              <button
                onClick={() => { setSelectedContent(row); setShowRejectModal(true); }}
                style={{
                  padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: '#FEE2E2', color: '#DC2626', fontSize: 12, fontWeight: 600,
                }}
                title="Reject"
              >
                ✕ Reject
              </button>
            </>
          ) : (
            <button
              onClick={() => handleRevert(row)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: '#FEF3C7', color: '#D97706', fontSize: 12, fontWeight: 600,
              }}
              title="Revert to Pending"
            >
              <MdUndo size={14} /> Revert
            </button>
          )}
          <button
            onClick={() => handleDelete(row.id)}
            style={{
              display: 'flex', padding: '6px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: '#F9FAFB', color: '#9CA3AF',
            }}
            title="Delete"
          >
            <MdDelete size={16} />
          </button>
        </div>
      ),
    },
  ];

  const tabs = [
    { id: 'pending', label: 'Pending', count: stats.pending },
    { id: 'approved', label: 'Approved', count: stats.approved },
    { id: 'rejected', label: 'Rejected', count: stats.rejected },
    { id: 'all', label: 'All', count: stats.total },
  ];

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-1 md:mb-2 font-poppins">
          Content Approval
        </h1>
        <p className="text-sm md:text-base text-gray-500">
          Preview and review content submitted by teachers before publishing
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        {[
          { label: 'Total Submissions', value: stats.total, colour: '#1E293B' },
          { label: 'Pending Review', value: stats.pending, colour: '#D97706' },
          { label: 'Approved', value: stats.approved, colour: '#059669' },
          { label: 'Rejected', value: stats.rejected, colour: '#DC2626' },
        ].map((s) => (
          <div key={s.label} className="bg-white p-3 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
            <p className="text-xs md:text-sm text-gray-500 mb-1">{s.label}</p>
            <p className="text-xl md:text-2xl font-bold" style={{ color: s.colour }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter Tabs (Non-white, colorful themed pill buttons) */}
      <div className="mb-4 md:mb-6">
        <div className="flex gap-2.5 overflow-x-auto scrollbar-hide py-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const tabThemes = {
              pending: {
                active: 'bg-amber-500 text-white border-amber-600 shadow-sm',
                inactive: 'bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200',
                countActive: 'bg-white/30 text-white',
                countInactive: 'bg-amber-200 text-amber-900',
              },
              approved: {
                active: 'bg-green-600 text-white border-green-700 shadow-sm',
                inactive: 'bg-green-100 text-green-900 border-green-300 hover:bg-green-200',
                countActive: 'bg-white/30 text-white',
                countInactive: 'bg-green-200 text-green-900',
              },
              rejected: {
                active: 'bg-rose-600 text-white border-rose-700 shadow-sm',
                inactive: 'bg-rose-100 text-rose-900 border-rose-300 hover:bg-rose-200',
                countActive: 'bg-white/30 text-white',
                countInactive: 'bg-rose-200 text-rose-900',
              },
              all: {
                active: 'bg-indigo-600 text-white border-indigo-700 shadow-sm',
                inactive: 'bg-indigo-100 text-indigo-900 border-indigo-300 hover:bg-indigo-200',
                countActive: 'bg-white/30 text-white',
                countInactive: 'bg-indigo-200 text-indigo-900',
              },
            };
            const theme = tabThemes[tab.id] || tabThemes.all;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-bold whitespace-nowrap border transition-all text-sm md:text-base flex-shrink-0 cursor-pointer ${
                  isActive ? theme.active : theme.inactive
                }`}
              >
                {tab.label}
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold ${
                  isActive ? theme.countActive : theme.countInactive
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 md:mb-6">
        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by topic, lesson, subject or teacher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
          />
        </div>
      </div>

      {/* Bulk Action Banner */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 md:p-4 mb-4 md:mb-6 bg-red-50 border border-red-200 rounded-xl shadow-sm">
          <div className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
              {selectedIds.length}
            </span>
            <span className="text-sm font-semibold text-red-900">
              {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'items'} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3.5 py-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm cursor-pointer"
            >
              Deselect All
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={actionLoading}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-lg shadow-sm transition-colors disabled:opacity-50 cursor-pointer"
            >
              <MdDelete size={16} />
              {actionLoading ? 'Deleting from Database...' : `Delete Selected (${selectedIds.length})`}
            </button>
          </div>
        </div>
      )}

      {/* Subject Navigation & View Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          {selectedSubject ? (
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                type="button"
                onClick={() => setSelectedSubject(null)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-xl font-bold text-xs shadow-xs transition-all cursor-pointer"
              >
                <MdArrowBack size={16} /> All Subjects
              </button>
              <span className="text-gray-300 font-bold">/</span>
              <div className="flex items-center gap-2">
                <span className="text-base md:text-lg font-bold text-darkNavy font-poppins">{selectedSubject}</span>
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  {displayedContents.length} {displayedContents.length === 1 ? 'Lesson' : 'Lessons'}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                {viewMode === 'subjects' 
                  ? `Curriculum by Subject (${subjectGroups.length} Active Subjects)`
                  : `All Content Table (${filteredContents.length} total items)`}
              </span>
            </div>
          )}
        </div>

        {/* View Switcher: Subject Cards vs Full Table */}
        <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
          {selectedSubject && (
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-xl text-xs font-semibold text-darkNavy outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer shadow-xs"
            >
              {subjectGroups.map((g) => (
                <option key={g.name} value={g.name}>
                  Switch to: {g.name} ({g.items.length})
                </option>
              ))}
            </select>
          )}

          <div className="flex items-center bg-gray-100 p-0.5 rounded-xl border border-gray-200">
            <button
              type="button"
              onClick={() => { setViewMode('subjects'); setSelectedSubject(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'subjects' && !selectedSubject
                  ? 'bg-white text-primary shadow-xs'
                  : 'text-gray-600 hover:text-darkNavy'
              }`}
            >
              <MdGridView size={15} /> Subjects
            </button>
            <button
              type="button"
              onClick={() => { setViewMode('all-table'); setSelectedSubject(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                viewMode === 'all-table'
                  ? 'bg-white text-primary shadow-xs'
                  : 'text-gray-600 hover:text-darkNavy'
              }`}
            >
              <MdViewList size={15} /> All Table
            </button>
          </div>
        </div>
      </div>

      {/* ── View 1: Subject Cards Grid ── */}
      {viewMode === 'subjects' && !selectedSubject ? (
        <div>
          {loading ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-xs">
              <span className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mb-2"></span>
              <p className="text-gray-400 font-medium text-sm">Loading curriculum subjects...</p>
            </div>
          ) : subjectGroups.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-xs">
              <MdMenuBook className="mx-auto text-4xl text-gray-300 mb-2" />
              <p className="text-gray-500 font-semibold text-base mb-1">No subjects found</p>
              <p className="text-xs text-gray-400">There are no submissions matching your active filter ({activeTab}).</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 mb-6">
              {subjectGroups.map((group) => {
                const theme = getSubjectTheme(group.name);
                return (
                  <div
                    key={group.name}
                    onClick={() => setSelectedSubject(group.name)}
                    style={{ background: theme.gradientStyle, padding: '18px 20px' }}
                    className={`${theme.border} ${theme.shadow} rounded-2xl border text-white transition-all cursor-pointer flex flex-col group transform hover:-translate-y-1 shadow-md`}
                  >
                    {/* Row 1: Subject Name on Left, Chevron Arrow on Right */}
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <h3 className="text-xl font-bold text-white tracking-tight leading-snug truncate">
                        {group.name}
                      </h3>
                      <div className={`w-8 h-8 rounded-xl ${theme.arrowBg} flex items-center justify-center transition-all shadow-xs flex-shrink-0`}>
                        <MdChevronRight size={20} />
                      </div>
                    </div>

                    {/* Row 2: Topic & Lesson count on Left, Approved Badge on Right (shaded area) */}
                    <div className="flex items-center justify-between gap-2 mb-2.5">
                      <p className={`text-xs ${theme.subtitleColor} font-semibold`}>
                        {group.topics.size} {group.topics.size === 1 ? 'Topic' : 'Topics'} · {group.items.length} {group.items.length === 1 ? 'Lesson' : 'Lessons'}
                      </p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {group.approvedCount > 0 && (
                          <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-100 border border-emerald-400/40 backdrop-blur-md shadow-2xs whitespace-nowrap">
                            ✓ {group.approvedCount} Approved
                          </span>
                        )}
                        {group.pendingCount > 0 && (
                          <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-amber-500/25 text-amber-100 border border-amber-400/40 backdrop-blur-md shadow-2xs whitespace-nowrap">
                            ⏳ {group.pendingCount} Pending
                          </span>
                        )}
                        {group.rejectedCount > 0 && (
                          <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-red-500/25 text-red-100 border border-red-400/40 backdrop-blur-md shadow-2xs whitespace-nowrap">
                            ✕ {group.rejectedCount} Rejected
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Teachers list */}
                    {group.teachers.size > 0 && (
                      <p className={`text-xs ${theme.subtitleColor} line-clamp-1 mb-3`}>
                        <span className="opacity-80 font-medium">Teacher:</span> <span className="font-bold text-white">{Array.from(group.teachers).join(', ')}</span>
                      </p>
                    )}

                    {/* Action Footer directly below content (no dead gap) */}
                    <div className={`pt-3 mt-auto border-t ${theme.borderColor} flex items-center justify-between text-xs font-bold`}>
                      <span className={`${theme.actionText} group-hover:underline flex items-center gap-1.5`}>
                        Manage Topics & Lessons
                      </span>
                      <span className={`${theme.actionText} group-hover:translate-x-1 transition-transform`}>
                        Open →
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ── View 2: Detailed Lessons Table & Mobile Cards (Filtered to Subject or All) ── */
        <div>
          {/* Mobile Cards */}
          <div className="block lg:hidden space-y-3 mb-4">
            {loading ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-400">Loading content...</p>
              </div>
            ) : displayedContents.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-400">No content found</p>
                {selectedSubject && (
                  <button
                    onClick={() => setSelectedSubject(null)}
                    className="mt-3 text-xs font-bold text-primary hover:underline"
                  >
                    ← Back to All Subjects
                  </button>
                )}
              </div>
            ) : (
              displayedContents.map((content) => {
                const lt = content.contentData?.lessonType || 'Text';
                const ti = getLessonTypeInfo(lt, content.type);
                const TI = ti.icon;
                return (
                  <div key={content.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="pt-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(content.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedIds((prev) =>
                              prev.includes(content.id) ? prev.filter((id) => id !== content.id) : [...prev, content.id]
                            );
                          }}
                          style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#2563EB' }}
                        />
                      </div>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 40, height: 40, borderRadius: 10,
                        background: ti.bg, color: ti.colour, flexShrink: 0,
                      }}>
                        <TI size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        {Boolean(content.contentData?.topicName || content.topicName) && (
                          <p className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-0.5">
                            {content.contentData?.topicName || content.topicName}
                          </p>
                        )}
                        <h3 className="font-semibold text-gray-900 text-base mb-0.5 truncate">
                          {content.title || 'Untitled'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {content.teacherName || 'N/A'} · {content.subjectName || content.subject || (content.subjectId ? (content.subjectId.charAt(0).toUpperCase() + content.subjectId.slice(1)) : 'N/A')}
                        </p>
                      </div>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: content.type === 'Topic' ? '#EEF2FF' : ti.bg,
                        color: content.type === 'Topic' ? '#4F46E5' : ti.colour,
                        flexShrink: 0,
                      }}>
                        {content.type === 'Topic' ? 'Curriculum Topic' : ti.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {new Date(content.createdAt?.toDate?.() || content.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span style={{
                          padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: content.status === 'approved' ? '#D1FAE5' : (content.status === 'rejected' ? '#FEE2E2' : '#FEF3C7'),
                          color: content.status === 'approved' ? '#059669' : (content.status === 'rejected' ? '#DC2626' : '#D97706'),
                        }}>
                          {content.status === 'approved' ? '✓ Approved' : (content.status === 'rejected' ? '✕ Rejected' : '⏳ Pending')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(content.id)}
                          className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <MdDelete size={17} />
                        </button>
                        <button
                          onClick={() => handleViewContent(content)}
                          className="px-4 py-1.5 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold flex items-center gap-1"
                        >
                          <MdRemoveRedEye size={15} /> Preview
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-12 text-center"><p className="text-gray-400">Loading content...</p></div>
              ) : displayedContents.length === 0 ? (
                <div className="p-12 text-center">
                  <p className="text-gray-400 mb-2">No content found</p>
                  {selectedSubject && (
                    <button
                      onClick={() => setSelectedSubject(null)}
                      className="text-xs font-bold text-primary hover:underline"
                    >
                      ← Back to All Subjects
                    </button>
                  )}
                </div>
              ) : (
                <DataTable columns={columns} data={displayedContents} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── View / Preview Modal ── */}
      <Modal
        isOpen={showViewModal}
        onClose={() => { setShowViewModal(false); setSelectedContent(null); }}
        title="Content Review"
        size="fullscreen"
      >
        {selectedContent && (
          <div className="content-review-layout">
            {/* Left: Meta Info */}
            <div className="review-meta-panel">
              <div className="review-meta-header">
                <div
                  className="review-type-icon"
                  style={{ background: typeInfo.bg, color: typeInfo.colour }}
                >
                  <typeInfo.icon size={28} />
                </div>
                <div>
                  <h3 className="review-content-title">{selectedContent.title || 'Untitled'}</h3>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: typeInfo.bg, color: typeInfo.colour, marginTop: 4,
                  }}>
                    {typeInfo.label}
                  </span>
                </div>
              </div>

              <div className="review-meta-grid">
                {/* Title / Topic Name (Admin Editable) */}
                <div className="review-meta-item" style={{ gridColumn: '1 / -1' }}>
                  <MdEdit size={16} color="#3B82F6" />
                  <div style={{ flex: 1 }}>
                    <span className="review-meta-label">Title / Topic Name (Admin Editable)</span>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="e.g. Modular Arithmetic"
                      className="w-full px-2 py-1 border border-blue-300 rounded text-sm mt-1 outline-none bg-blue-50 font-semibold text-blue-900 focus:bg-white focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="review-meta-item">
                  <MdPerson size={16} color="#94A3B8" />
                  <div>
                    <span className="review-meta-label">Teacher</span>
                    <span className="review-meta-value">
                      {selectedContent.teacherName || selectedContent.teacher || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="review-meta-item">
                  <MdSchool size={16} color="#94A3B8" />
                  <div>
                    <span className="review-meta-label">Class</span>
                    <span className="review-meta-value">
                      {selectedContent.className || selectedContent.classLevel || selectedContent.class || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="review-meta-item">
                  <MdBook size={16} color="#94A3B8" />
                  <div>
                    <span className="review-meta-label">Subject</span>
                    <span className="review-meta-value">
                      {selectedContent.subjectName || selectedContent.subject || (selectedContent.subjectId ? (selectedContent.subjectId.charAt(0).toUpperCase() + selectedContent.subjectId.slice(1)) : 'N/A')}
                    </span>
                  </div>
                </div>
                <div className="review-meta-item">
                  <MdLabel size={16} color="#94A3B8" />
                  <div>
                    <span className="review-meta-label">Submission Type</span>
                    <span className="review-meta-value">{selectedContent.type || 'Lesson'}</span>
                  </div>
                </div>

                {/* Term — always editable for admin */}
                {selectedContent.type === 'Lesson' && (
                  <div className="review-meta-item">
                    <MdLabel size={16} color="#94A3B8" />
                    <div>
                      <span className="review-meta-label">Term (Admin Editable)</span>
                      <select
                        value={editTerm}
                        onChange={(e) => setEditTerm(e.target.value)}
                        className="px-2 py-1 border border-blue-300 rounded text-sm mt-1 outline-none bg-blue-50 font-semibold text-blue-800"
                      >
                        <option value="1">Term 1</option>
                        <option value="2">Term 2</option>
                        <option value="3">Term 3</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Time Limit — always editable for admin */}
                {selectedContent.type === 'Quiz' && (
                  <div className="review-meta-item">
                    <MdSchedule size={16} color="#94A3B8" />
                    <div>
                      <span className="review-meta-label">Time Limit (mins) (Admin Editable)</span>
                      <input
                        type="number"
                        value={editTimeLimit}
                        onChange={(e) => setEditTimeLimit(e.target.value)}
                        placeholder={selectedContent.contentData?.durationMinutes || selectedContent.contentData?.timeLimit || '5'}
                        className="px-2 py-1 border border-blue-300 rounded text-sm mt-1 w-20 outline-none bg-blue-50 font-semibold text-blue-800"
                      />
                    </div>
                  </div>
                )}
                <div className="review-meta-item">
                  <MdSchedule size={16} color="#94A3B8" />
                  <div>
                    <span className="review-meta-label">Submitted On</span>
                    <span className="review-meta-value">
                      {new Date(
                        selectedContent.createdAt?.toDate?.() || selectedContent.createdAt
                      ).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                </div>

                {/* Status selector — always editable for admin */}
                <div className="review-meta-item">
                  <MdLabel size={16} color="#94A3B8" />
                  <div>
                    <span className="review-meta-label">Status (Admin Changeable)</span>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="px-2 py-1 border border-blue-300 rounded text-sm mt-1 outline-none bg-blue-50 font-semibold text-blue-800"
                    >
                      <option value="pending">⏳ Pending Review</option>
                      <option value="approved">✓ Approved</option>
                      <option value="rejected">✕ Rejected</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Status badge */}
              <div className="review-status-row">
                {(() => {
                  const statusMap = {
                    pending: { bg: '#FEF3C7', colour: '#D97706', label: '⏳ Pending Review' },
                    approved: { bg: '#D1FAE5', colour: '#059669', label: '✓ Approved' },
                    rejected: { bg: '#FEE2E2', colour: '#DC2626', label: '✕ Rejected' },
                  };
                  const s = statusMap[selectedContent.status] || statusMap.pending;
                  return (
                    <span style={{
                      padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                      background: s.bg, color: s.colour,
                    }}>
                      {s.label}
                    </span>
                  );
                })()}
              </div>

              {/* Rejection reason if rejected */}
              {selectedContent.status === 'rejected' && selectedContent.rejectionReason && (
                <div className="rejection-reason-box">
                  <p className="rejection-reason-label">Rejection Reason:</p>
                  <p className="rejection-reason-text">{selectedContent.rejectionReason}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="review-action-btns" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                {/* Pending: Reject or Approve & Publish */}
                {selectedContent.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={actionLoading}
                      className="review-btn reject-btn"
                      style={{ flex: 1 }}
                    >
                      <MdClose size={18} /> Reject Content
                    </button>
                    <button
                      onClick={() => handleApprove(selectedContent)}
                      disabled={actionLoading}
                      className="review-btn approve-btn"
                      style={{ flex: 1 }}
                    >
                      <MdCheckCircle size={18} />
                      {actionLoading ? 'Approving…' : 'Approve & Publish'}
                    </button>
                  </div>
                )}

                {/* Approved: Revert to Pending or Reject */}
                {selectedContent.status === 'approved' && (
                  <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                    <button
                      onClick={() => handleRevert(selectedContent)}
                      disabled={actionLoading}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        background: '#FEF3C7',
                        color: '#D97706',
                        border: '1px solid #FCD34D',
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                      }}
                    >
                      <MdUndo size={18} /> Revert to Pending
                    </button>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={actionLoading}
                      className="review-btn reject-btn"
                      style={{ flex: 1 }}
                    >
                      <MdClose size={18} /> Reject Content
                    </button>
                  </div>
                )}

                {/* Rejected: Revert to Pending or Approve */}
                {selectedContent.status === 'rejected' && (
                  <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                    <button
                      onClick={() => handleRevert(selectedContent)}
                      disabled={actionLoading}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        background: '#FEF3C7',
                        color: '#D97706',
                        border: '1px solid #FCD34D',
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                      }}
                    >
                      <MdUndo size={18} /> Revert to Pending
                    </button>
                    <button
                      onClick={() => handleApprove(selectedContent)}
                      disabled={actionLoading}
                      className="review-btn approve-btn"
                      style={{ flex: 1 }}
                    >
                      <MdCheckCircle size={18} />
                      {actionLoading ? 'Approving…' : 'Approve & Publish'}
                    </button>
                  </div>
                )}

                {/* Save Changes button — for updating term/timeLimit or changing status dropdown */}
                <button
                  onClick={handleSaveOverride}
                  disabled={actionLoading}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: '#3B82F6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    opacity: actionLoading ? 0.7 : 1,
                  }}
                >
                  <MdCheckCircle size={18} />
                  {actionLoading ? 'Saving…' : 'Save Admin Changes'}
                </button>
              </div>
            </div>

            {/* Right: Content Preview */}
            <div className="review-preview-panel">
              <div className="preview-panel-header">
                <span>Content Preview</span>
                <span className="preview-panel-hint">
                  Watch / read the full content before making a decision
                </span>
              </div>
              <ContentPreview
                content={selectedContent}
                onContentUpdated={(updatedFields) => {
                  setSelectedContent((prev) => ({
                    ...prev,
                    ...updatedFields,
                    contentData: {
                      ...(prev?.contentData || {}),
                      ...(updatedFields?.contentData || {}),
                    },
                  }));
                }}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => { setShowRejectModal(false); setRejectReason(''); }}
        title="Reject Content"
        size="small"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Provide clear feedback to the teacher so they can improve and resubmit..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none text-sm"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={!rejectReason.trim() || actionLoading}
              className="px-6 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <MdClose size={16} />
              {actionLoading ? 'Rejecting…' : 'Reject Content'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Inline styles for the review layout & preview */}
      <style>{`
        /* ── Modal Fullscreen size ── */
        .modal-fullscreen {
          width: 98vw !important;
          max-width: 98vw !important;
          height: 96vh !important;
          max-height: 96vh !important;
          border-radius: 20px !important;
        }

        /* ── Modal XLarge size ── */
        .modal-xlarge {
          width: 100%;
          max-width: 1100px;
        }

        /* ── Two-column review layout ── */
        .content-review-layout {
          display: grid;
          grid-template-columns: 340px 1fr;
          gap: 0;
          min-height: calc(96vh - 120px);
        }

        /* ── Left meta panel ── */
        .review-meta-panel {
          padding: 8px 24px 8px 0;
          border-right: 1px solid #E2E8F0;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .review-meta-header {
          display: flex;
          align-items: flex-start;
          gap: 14px;
        }

        .review-type-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 52px;
          height: 52px;
          border-radius: 14px;
          flex-shrink: 0;
        }

        .review-content-title {
          font-size: 17px;
          font-weight: 700;
          color: #111827;
          line-height: 1.3;
          margin: 0;
        }

        .review-meta-grid {
          display: flex;
          flex-direction: column;
          gap: 14px;
          background: #F8FAFC;
          border-radius: 12px;
          padding: 16px;
        }

        .review-meta-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .review-meta-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94A3B8;
          margin-bottom: 2px;
        }

        .review-meta-value {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #1E293B;
        }

        .review-status-row {
          display: flex;
          align-items: center;
        }

        .rejection-reason-box {
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 10px;
          padding: 12px 14px;
        }

        .rejection-reason-label {
          font-size: 12px;
          font-weight: 700;
          color: #DC2626;
          margin: 0 0 6px;
        }

        .rejection-reason-text {
          font-size: 13px;
          color: #7F1D1D;
          margin: 0;
          line-height: 1.5;
        }

        .review-action-btns {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: auto;
        }

        .review-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          border-radius: 12px;
          border: none;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .review-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .approve-btn {
          background: #059669;
          color: #fff;
        }

        .approve-btn:hover:not(:disabled) {
          background: #047857;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(5,150,105,0.3);
        }

        .reject-btn {
          background: #FEE2E2;
          color: #DC2626;
        }

        .reject-btn:hover:not(:disabled) {
          background: #FECACA;
        }

        /* ── Right preview panel ── */
        .review-preview-panel {
          padding: 8px 0 8px 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .preview-panel-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
          font-size: 15px;
          font-weight: 700;
          color: #1E293B;
        }

        .preview-panel-hint {
          font-size: 12px;
          font-weight: 400;
          color: #94A3B8;
        }

        /* ── ContentPreview blocks ── */
        .content-preview-block {
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex: 1;
        }

        .preview-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 700;
          width: fit-content;
        }

        /* Video */
        .video-wrapper {
          position: relative;
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          background: #000;
          aspect-ratio: 16/9;
        }

        .video-iframe,
        .video-player {
          position: absolute;
          top: 0; left: 0;
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 12px;
        }

        /* PDF / PPT */
        .doc-wrapper {
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #E2E8F0;
          height: 480px;
        }

        .doc-iframe {
          width: 100%;
          height: 100%;
          border: none;
        }

        /* No preview */
        .no-preview-box {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: #F8FAFC;
          border: 2px dashed #E2E8F0;
          border-radius: 12px;
          padding: 48px 24px;
          min-height: 200px;
        }

        .no-preview-box p {
          color: #94A3B8;
          font-size: 14px;
          margin: 0;
        }

        /* Open link button */
        .open-link-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 18px;
          background: #2563EB;
          color: #fff;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          transition: background 0.2s;
        }

        .open-link-btn:hover { background: #1D4ED8; }

        .open-link-btn.outline {
          background: transparent;
          color: #2563EB;
          border: 1.5px solid #2563EB;
        }

        .open-link-btn.outline:hover { background: #EFF6FF; }

        /* Media URL row */
        .media-url-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .media-url-label {
          font-size: 12px;
          font-weight: 600;
          color: #94A3B8;
          white-space: nowrap;
        }

        .media-url-link {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #2563EB;
          text-decoration: none;
          word-break: break-all;
        }

        .media-url-link:hover { text-decoration: underline; }

        /* Text preview */
        .text-body-preview {
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          border-radius: 12px;
          padding: 20px 24px;
          max-height: 420px;
          overflow-y: auto;
          font-size: 14px;
          color: #374151;
          line-height: 1.75;
        }

        .no-text-content {
          color: #94A3B8 !important;
          font-style: italic;
        }

        /* ── Responsive collapse to single column ── */
        @media (max-width: 900px) {
          .content-review-layout {
            grid-template-columns: 1fr;
          }
          .review-meta-panel {
            border-right: none;
            border-bottom: 1px solid #E2E8F0;
            padding: 0 0 20px;
          }
          .review-preview-panel {
            padding: 20px 0 0;
          }
          .review-action-btns {
            flex-direction: row;
          }
          .review-btn {
            flex: 1;
          }
          .modal-xlarge {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ContentApproval;
